import { useMemo, useState } from 'react';
import { BookEngineNav } from '../components/editor/BookEngineNav';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useAddOutlineNode,
  useCreateOutline,
  useDeleteOutlineNode,
  useOutlinesForBook,
  usePromoteOutlineToChapters,
  type OutlineDto,
  type OutlineNodeDto,
} from '../api/queries/outlines';
import { useStoryArchitectSession } from '../api/queries/storyArchitect';
import { useMarkLastEngine } from '../api/queries/bookSession';
import { useBoardsForBook } from '../api/queries/boards';
import { GenerationModal } from '../components/ai/GenerationModal';

export function OutlineRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  useMarkLastEngine(bookId, 'outline');
  const outlines = useOutlinesForBook(bookId);
  const { data: storySession } = useStoryArchitectSession(bookId);
  const boards = useBoardsForBook(bookId);
  const addNode = useAddOutlineNode(bookId);
  const [activeOutlineId, setActiveOutlineId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importBoardDialogOpen, setImportBoardDialogOpen] = useState(false);
  const [importingBoard, setImportingBoard] = useState(false);
  const [importing, setImporting] = useState(false);
  const [snackMsg, setSnackMsg] = useState<string | null>(null);

  // AI Generation Modal state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSeedPrompt, setAiSeedPrompt] = useState('');

  const beats = useMemo(() => storySession?.beats ?? [], [storySession]);

  // Find the first board with notes for the "Import from Board" feature
  const boardWithNotes = useMemo(() => {
    if (!boards.data) return null;
    return boards.data.find((b) => b.notes.length > 0) ?? null;
  }, [boards.data]);

  const active = useMemo(
    () =>
      outlines.data?.find((o) => o.id === activeOutlineId) ??
      outlines.data?.[0] ??
      null,
    [outlines.data, activeOutlineId],
  );

  const handleImportBeats = async () => {
    if (!active || beats.length === 0) return;
    setImporting(true);
    try {
      for (const beat of beats) {
        await addNode.mutateAsync({
          outlineId: active.id,
          parentId: null,
          name: beat.name,
          description: beat.description ?? undefined,
        });
      }
      setSnackMsg(`Imported ${beats.length} beats as outline nodes.`);
    } catch {
      setSnackMsg('Failed to import some beats.');
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
    }
  };

  const handleImportBoard = async () => {
    if (!active || !boardWithNotes) return;
    setImportingBoard(true);
    try {
      for (const note of boardWithNotes.notes) {
        const content = note.content as
          | { title?: string; text?: string }
          | undefined;
        const name = content?.title || content?.text || `Note`;
        const description =
          content?.text && content?.title ? content.text : undefined;
        await addNode.mutateAsync({
          outlineId: active.id,
          parentId: null,
          name,
          description,
        });
      }
      setSnackMsg(
        `Imported ${boardWithNotes.notes.length} notes from "${boardWithNotes.name}" as outline nodes.`,
      );
    } catch {
      setSnackMsg('Failed to import some board notes.');
    } finally {
      setImportingBoard(false);
      setImportBoardDialogOpen(false);
    }
  };

  const handleExpandChapter = () => {
    if (!active || active.nodes.length === 0) return;
    // Use the first top-level node as the "selected chapter"
    const topLevelNodes = active.nodes.filter((n) => !n.parentId);
    if (topLevelNodes.length === 0) return;
    const node = topLevelNodes[0];
    setAiSeedPrompt(
      `Given this chapter outline: "${node.name} - ${node.description || '(no description)'}", suggest 4-6 paragraph descriptions that would flesh out this chapter. For each paragraph, provide a brief description of what happens.`,
    );
    setAiModalOpen(true);
  };

  const handleFillGaps = () => {
    if (!active) return;
    const nodesSummary = active.nodes
      .filter((n) => !n.parentId)
      .map((n) => `- ${n.name}: ${n.description || '(no description)'}`)
      .join('\n');
    setAiSeedPrompt(
      `Review this outline and suggest any missing chapters or scenes that would improve the narrative flow:\n${nodesSummary}`,
    );
    setAiModalOpen(true);
  };

  if (outlines.isLoading) return <CircularProgress />;
  if (outlines.isError)
    return <Alert severity="error">Failed to load outlines.</Alert>;

  return (
    <div>
      <BookEngineNav bookId={bookId} />
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 3, mt: 2 }}
      >
        <Typography variant="h4" sx={{ flex: 1 }}>
          Outline Builder
        </Typography>
        {active && active.nodes.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={handleExpandChapter}
          >
            Expand Chapter
          </Button>
        )}
        {active && (
          <Button
            variant="outlined"
            startIcon={<PlaylistAddIcon />}
            onClick={handleFillGaps}
          >
            Fill Gaps
          </Button>
        )}
        {active && beats.length > 0 && (
          <Button variant="outlined" onClick={() => setImportDialogOpen(true)}>
            Import from Beats
          </Button>
        )}
        {active && boardWithNotes && (
          <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            onClick={() => setImportBoardDialogOpen(true)}
          >
            Import from Board
          </Button>
        )}
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New outline
        </Button>
      </Stack>

      {(outlines.data ?? []).length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No outlines yet. Create one to plan your chapter structure, then
          promote to real chapters with one click.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {(outlines.data ?? []).map((o) => (
          <Button
            key={o.id}
            size="small"
            variant={active?.id === o.id ? 'contained' : 'outlined'}
            onClick={() => setActiveOutlineId(o.id)}
          >
            {o.name}
          </Button>
        ))}
      </Stack>

      {active && <OutlineEditor bookId={bookId} outline={active} />}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New outline</DialogTitle>
        <DialogContent>
          <CreateOutlineForm
            bookId={bookId}
            onCreated={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Import from Beats confirmation */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Import from Beats</DialogTitle>
        <DialogContent>
          <Typography>
            Import {beats.length} beat{beats.length === 1 ? '' : 's'} as outline
            chapters?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Each beat will be added as a top-level node in the &quot;
            {active?.name}&quot; outline, preserving the beat order.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImportBeats}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import from Board confirmation */}
      <Dialog
        open={importBoardDialogOpen}
        onClose={() => setImportBoardDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Import from Board</DialogTitle>
        <DialogContent>
          <Typography>
            Import {boardWithNotes?.notes.length ?? 0} note
            {(boardWithNotes?.notes.length ?? 0) === 1 ? '' : 's'} from board
            &quot;{boardWithNotes?.name}&quot; as outline nodes?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Each note will be added as a top-level node in the &quot;
            {active?.name}&quot; outline.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportBoardDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportBoard}
            disabled={importingBoard}
          >
            {importingBoard ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generation Modal */}
      <GenerationModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        mode="text"
        bookId={bookId}
        seedPrompt={aiSeedPrompt}
      />

      <Snackbar
        open={snackMsg !== null}
        autoHideDuration={4000}
        onClose={() => setSnackMsg(null)}
        message={snackMsg}
      />
    </div>
  );
}

function CreateOutlineForm({
  bookId,
  onCreated,
}: {
  bookId: string;
  onCreated: () => void;
}) {
  const create = useCreateOutline();
  const [name, setName] = useState('');
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <Button
        variant="contained"
        disabled={!name.trim() || create.isPending}
        onClick={async () => {
          await create.mutateAsync({ projectId: bookId, name: name.trim() });
          setName('');
          onCreated();
        }}
      >
        Create
      </Button>
    </Stack>
  );
}

function OutlineEditor({
  bookId,
  outline,
}: {
  bookId: string;
  outline: OutlineDto;
}) {
  const navigate = useNavigate();
  const addNode = useAddOutlineNode(bookId);
  const remove = useDeleteOutlineNode(bookId);
  const promote = usePromoteOutlineToChapters(bookId);
  const [newName, setNewName] = useState('');
  const [selectedParent, setSelectedParent] = useState<string | null>(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [generateDraft, setGenerateDraft] = useState(false);
  const [promoteSnack, setPromoteSnack] = useState<string | null>(null);

  const byParent = useMemo(() => {
    const m = new Map<string | null, OutlineNodeDto[]>();
    for (const n of outline.nodes) {
      const key = n.parentId ?? null;
      (m.get(key) ?? m.set(key, []).get(key)!).push(n);
    }
    for (const arr of m.values())
      arr.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    return m;
  }, [outline.nodes]);

  const handlePromote = async () => {
    const ids = await promote.mutateAsync(outline.id);
    setPromoteDialogOpen(false);
    if (generateDraft) {
      setPromoteSnack(
        `Created ${ids.length} chapters. Open the Book Editor and use AI Expand on each paragraph to generate prose.`,
      );
    } else {
      setPromoteSnack(`Created ${ids.length} chapters.`);
    }
    setGenerateDraft(false);
    // Navigate to the Book Editor so the user can work with the new chapters
    navigate({ to: '/books/$bookId', params: { bookId } });
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {outline.name}
          </Typography>
          <Button
            variant="outlined"
            disabled={promote.isPending}
            onClick={() => setPromoteDialogOpen(true)}
          >
            Promote to chapters
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={selectedParent ? 'New child node' : 'New top-level node'}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button
            variant="contained"
            disabled={!newName.trim() || addNode.isPending}
            onClick={async () => {
              await addNode.mutateAsync({
                outlineId: outline.id,
                parentId: selectedParent,
                name: newName.trim(),
              });
              setNewName('');
            }}
          >
            Add
          </Button>
          {selectedParent && (
            <Button onClick={() => setSelectedParent(null)}>
              Clear parent
            </Button>
          )}
        </Stack>

        <OutlineTree
          parentId={null}
          byParent={byParent}
          selectedParent={selectedParent}
          onSelectParent={setSelectedParent}
          onDelete={(id) => remove.mutate(id)}
        />
      </CardContent>

      {/* Promote confirmation dialog */}
      <Dialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Promote to chapters</DialogTitle>
        <DialogContent>
          <Typography>
            Create chapters for all unlinked leaf nodes in &quot;{outline.name}
            &quot;?
          </Typography>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={generateDraft}
                onChange={(e) => setGenerateDraft(e.target.checked)}
              />
            }
            label="Generate AI draft prose for each paragraph"
          />
          {generateDraft && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
              After chapters are created you will be taken to the Book Editor.
              Use AI Expand on each paragraph to generate prose.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePromote}
            disabled={promote.isPending}
          >
            {promote.isPending ? 'Promoting...' : 'Promote'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={promoteSnack !== null}
        autoHideDuration={6000}
        onClose={() => setPromoteSnack(null)}
        message={promoteSnack}
      />
    </Card>
  );
}

function OutlineTree({
  parentId,
  byParent,
  selectedParent,
  onSelectParent,
  onDelete,
  depth = 0,
}: {
  parentId: string | null;
  byParent: Map<string | null, OutlineNodeDto[]>;
  selectedParent: string | null;
  onSelectParent: (id: string | null) => void;
  onDelete: (id: string) => void;
  depth?: number;
}) {
  const items = byParent.get(parentId) ?? [];
  if (items.length === 0) return null;
  return (
    <List dense disablePadding>
      {items.map((n) => (
        <Box key={n.id}>
          <ListItemButton
            selected={selectedParent === n.id}
            onClick={() =>
              onSelectParent(selectedParent === n.id ? null : n.id)
            }
            sx={{ pl: 1 + depth * 2 }}
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{n.name}</Typography>
                  {n.linkedChapterId && (
                    <Typography variant="caption" color="success.main">
                      · linked
                    </Typography>
                  )}
                </Stack>
              }
              secondary={n.description ?? undefined}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(n.id);
              }}
              aria-label="delete"
              color="error"
            >
              ✕
            </IconButton>
          </ListItemButton>
          <OutlineTree
            parentId={n.id}
            byParent={byParent}
            selectedParent={selectedParent}
            onSelectParent={onSelectParent}
            onDelete={onDelete}
            depth={depth + 1}
          />
        </Box>
      ))}
    </List>
  );
}
